import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Check, MessageCircle, CreditCard, Smartphone, Wallet, Landmark } from "lucide-react";
import axios from "axios";
import { useApp } from "@/context/AppContext";
import { WatchConfigPreview } from "@/components/WatchConfigPreview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYMENT_METHODS = [
  { id: "card", Icon: CreditCard },
  { id: "twint", Icon: Smartphone },
  { id: "paypal", Icon: Wallet },
  { id: "bank", Icon: Landmark },
];

const TYPE_ICON = { card: CreditCard, twint: Smartphone, paypal: Wallet, bank: Landmark, other: Wallet };

const Checkout = () => {
  const { t, cart, cartTotal, clearCart, paymentMethods } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", address: "" });
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [loading, setLoading] = useState(false);
  const [orderNumber, setOrderNumber] = useState(null);

  // Methods added in the admin area win; otherwise fall back to the defaults.
  const methods = paymentMethods.length > 0
    ? paymentMethods.map((m) => ({ id: m.id, label: m.label, details: m.details, Icon: TYPE_ICON[m.type] || Wallet, apiValue: m.label }))
    : PAYMENT_METHODS.map(({ id, Icon }) => ({ id, label: t.checkout.paymentMethods[id], details: "", Icon, apiValue: id }));
  const activeMethod = methods.find((m) => m.id === paymentMethod) || methods[0];

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/orders`, {
        customer_name: form.name,
        customer_email: form.email,
        customer_address: form.address,
        items: cart,
        total: cartTotal,
        payment_method: activeMethod.apiValue,
      });
      setOrderNumber(res.data.order_number);
      clearCart();
    } catch {
      alert("Fehler beim Absenden");
    } finally {
      setLoading(false);
    }
  };

  if (orderNumber) {
    return (
      <main data-testid="order-success-page" className="pt-32 pb-24 min-h-screen bg-[#170F09] flex items-center">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="w-20 h-20 mx-auto rounded-full border border-[#C9A36B] flex items-center justify-center mb-8">
            <Check size={30} className="text-[#C9A36B]" strokeWidth={1.5} />
          </div>
          <div className="kicker mb-4">✓ {t.checkout.orderPlaced}</div>
          <h1 className="section-heading text-4xl md:text-5xl mb-6">{t.checkout.thankYou}</h1>
          <div className="border-y border-white/10 py-6 my-10">
            <div className="kicker mb-1">{t.checkout.orderNumber}</div>
            <div data-testid="order-number" className="font-mono text-2xl text-[#C9A36B]">{orderNumber}</div>
          </div>
          {activeMethod.details && (
            <div data-testid="order-payment-details" className="mb-10 border border-[#C9A36B]/40 bg-[#C9A36B]/[0.06] p-4 text-left text-sm text-white/85 break-words">
              <span className="kicker block text-[0.6rem] mb-1">{t.checkout.paymentTitle}: {activeMethod.label}</span>
              {activeMethod.details}
            </div>
          )}
          <Link to="/" className="btn-champagne">{t.checkout.backHome}</Link>
        </div>
      </main>
    );
  }

  if (cart.length === 0) {
    return (
      <main className="pt-32 pb-24 min-h-screen bg-[#170F09] flex items-center">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="text-white/50 mb-6">{t.cart.empty}</div>
          <Link to="/configurator" className="btn-champagne">{t.cart.startConfig}</Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="checkout-page" className="pt-32 pb-24 min-h-screen bg-[#170F09]">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="mb-12">
          <div className="kicker kicker-eyebrow mb-3">{t.checkout.title}</div>
          <h1 className="section-heading text-4xl md:text-5xl">{t.checkout.customerInfo}</h1>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <form onSubmit={submit} className="border border-white/8 p-8 space-y-6">
            <div>
              <label className="kicker mb-2 block">{t.checkout.fullName}</label>
              <input
                data-testid="checkout-name-input"
                required value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full bg-transparent border-b border-white/25 focus:border-[#C9A36B] py-2 text-white outline-none"
              />
            </div>
            <div>
              <label className="kicker mb-2 block">{t.checkout.email}</label>
              <input
                data-testid="checkout-email-input"
                required type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                className="w-full bg-transparent border-b border-white/25 focus:border-[#C9A36B] py-2 text-white outline-none"
              />
            </div>
            <div>
              <label className="kicker mb-2 block">{t.checkout.address}</label>
              <textarea
                data-testid="checkout-address-input"
                required rows={4} value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))}
                className="w-full bg-transparent border-b border-white/25 focus:border-[#C9A36B] py-2 text-white outline-none resize-none"
              />
            </div>
            <div data-testid="payment-method-select">
              <label className="kicker mb-3 block">{t.checkout.paymentTitle}</label>
              <div className="grid grid-cols-2 gap-3">
                {methods.map(({ id, label, Icon }) => (
                  <button
                    key={id}
                    type="button"
                    data-testid={`payment-method-${id}`}
                    onClick={() => setPaymentMethod(id)}
                    aria-pressed={activeMethod.id === id}
                    className={`pill-option justify-start ${activeMethod.id === id ? "active" : ""}`}
                  >
                    <Icon size={18} strokeWidth={1.5} className="shrink-0" />
                    <span className="truncate">{label}</span>
                  </button>
                ))}
              </div>
              {activeMethod.details && (
                <div data-testid="payment-details" className="mt-3 border border-[#C9A36B]/40 bg-[#C9A36B]/[0.06] p-3 text-sm text-white/85 break-words">
                  <span className="kicker block text-[0.6rem] mb-1">{activeMethod.label}</span>
                  {activeMethod.details}
                </div>
              )}
              <p className="text-[0.7rem] text-white/40 leading-relaxed mt-3">{t.checkout.feeNotice}</p>
            </div>

            <Link
              to="/contact"
              data-testid="payment-notice"
              className="flex items-start gap-3 border border-white/10 bg-white/[0.02] p-4 hover:border-[#C9A36B]/40 transition-colors group"
            >
              <MessageCircle size={20} strokeWidth={1.5} className="text-[#C9A36B] shrink-0 mt-0.5" />
              <div className="text-sm text-white/70 leading-snug">{t.checkout.paymentNotice}</div>
            </Link>
            <button
              data-testid="place-order-button"
              type="submit" disabled={loading}
              className="btn-champagne w-full disabled:opacity-60"
            >{loading ? "..." : `${t.checkout.placeOrder} — CHF ${cartTotal.toFixed(2)}`}</button>
          </form>

          <div className="border border-white/8 p-8">
            <div className="kicker mb-6">{t.cart.title}</div>
            <div className="space-y-4">
              {cart.map(item => (
                <div key={item.id} className="flex gap-4 border-b border-white/8 pb-4">
                  <div className="w-16 h-20 bg-white flex items-center justify-center shrink-0 overflow-hidden">
                    {item.type === "custom" && item.config ? (
                      <WatchConfigPreview config={item.config} className="w-full h-full p-1" testId={`checkout-preview-${item.id}`} />
                    ) : item.image ? (
                      <img src={item.image} alt={item.name} className="w-full h-full object-contain p-1" />
                    ) : null}
                  </div>
                  <div className="flex-1">
                    <div className="font-heading">{item.name}</div>
                    <div className="kicker mt-1">× {item.quantity}</div>
                  </div>
                  <div className="font-mono text-sm">CHF {(item.price * item.quantity).toFixed(2)}</div>
                </div>
              ))}
            </div>
            <div className="mt-6 space-y-2">
              <div className="flex justify-between text-sm text-white/60"><span>{t.cart.subtotal}</span><span className="font-mono">CHF {cartTotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-sm text-white/60"><span>{t.cart.shipping}</span><span className="font-mono text-[#C9A36B]">{t.cart.free}</span></div>
              <div className="flex justify-between text-lg pt-3 border-t border-white/10"><span className="font-heading">{t.cart.total}</span><span data-testid="checkout-total" className="font-mono text-[#C9A36B]">CHF {cartTotal.toFixed(2)}</span></div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Checkout;
